import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import {
  CommonActions,
  useNavigation,
  useNavigationState,
} from '@react-navigation/native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import MaterialIcons from '@react-native-vector-icons/material-icons/static';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import AntDesign from '@react-native-vector-icons/ant-design/static';
import { useDispatch } from 'react-redux';
import { useTheme, GTFonts, withOpacity } from '../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../utils/libs';
import { WEB_NAV_SIDEBAR_RAIL_S } from '../../theme/webLayoutWidths';
import { resetBackofficeUnlock } from '../../redux/securityRuntimeSlice';

type IconLibrary =
  | 'MaterialCommunityIcons'
  | 'MaterialIcons'
  | 'Ionicons'
  | 'AntDesign';

type SideNavItemProps = {
  label: string;
  icon: string;
  iconLibrary?: IconLibrary;
  isActive: boolean;
  onPress: () => void;
  styles: any;
  theme: any;
};

const SideNavItem = React.memo(
  ({
    label,
    icon,
    iconLibrary = 'MaterialCommunityIcons',
    isActive,
    onPress,
    styles,
    theme,
  }: SideNavItemProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const IconComponent =
      iconLibrary === 'Ionicons'
        ? Ionicons
        : iconLibrary === 'MaterialIcons'
          ? MaterialIcons
          : iconLibrary === 'AntDesign'
            ? AntDesign
            : MaterialCommunityIcons;

    const iconColor = isActive
      ? theme.colors.primaryDark
      : isHovered
        ? theme.colors.primary
        : theme.colors.textGray600;

    return (
      <Pressable
        onPress={onPress}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        style={[
          styles.sideNavItem,
          isActive && styles.sideNavItemActive,
          isHovered && !isActive && styles.sideNavItemHovered,
        ]}>
        <View style={styles.sideNavIconWrapper}>
          <IconComponent
            name={icon as any}
            size={iconSize(22)}
            color={iconColor}
          />
        </View>
        <Text
          style={[
            styles.sideNavItemText,
            isActive && styles.sideNavItemTextActive,
            isHovered && !isActive && styles.sideNavItemTextHovered,
          ]}>
          {label}
        </Text>
      </Pressable>
    );
  },
);

function getActiveRouteName(state: any): string {
  if (!state || !Array.isArray(state.routes) || typeof state.index !== 'number')
    return '';
  const route = state.routes[state.index];
  if (!route) return '';
  if (route.state) return getActiveRouteName(route.state);
  return String(route.name || '');
}

export default function BackOfficeSidebar() {
  const navigation = useNavigation<any>();
  const navState = useNavigationState(s => s);
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const activeLeafRoute = useMemo(
    () => getActiveRouteName(navState),
    [navState],
  );

  const activeNavId = useMemo(() => {
    switch (activeLeafRoute) {
      case 'Dashboard':
      case 'BackOfficeDashboard':
        return 'dashboard';
      case 'Staff':
      case 'Customers':
      case 'People':
      case 'BackOfficeEmployees':
      case 'BackOfficeEmployeeView':
      case 'BackOfficeEmployeeDetails':
      case 'BackOfficeAddEmployee':
      case 'BackOfficeCustomers':
      case 'BackOfficeCustomerDetails':
      case 'BackOfficeCustomerOrders':
      // Nested tabs inside BackOfficeCustomerOrdersScreen
      case 'CustomerCompletedOrders':
      case 'CustomerPendingOrders':
      case 'CustomerPartialOrders':
      case 'BackOfficeAddCustomer':
        return 'people';
      case 'Inventory':
      case 'BackOfficeProducts':
      case 'BackOfficeProductDetails':
      case 'BackOfficeProductView':
      case 'BackOfficeVariantView':
      case 'BackOfficeAddProduct':
        return 'products';
      case 'Orders':
      case 'BackOfficeOrders':
      case 'BackOfficeOrderDetails':
      // Nested tabs inside BackOfficeOrdersTabNavigator
      case 'Completed':
      case 'Pending':
      case 'Partially Paid':
        return 'orders';
      case 'Sales':
      case 'BackOfficeSales':
        return 'sales';
      case 'Suppliers':
      case 'BackOfficeSuppliers':
      case 'BackOfficeAddSupplier':
      case 'BackOfficeSupplierDetails':
      case 'BackOfficePurchaseOrderUpsert':
      case 'BackOfficePurchaseOrderDetails':
        return 'suppliers';
      case 'Expenses':
      case 'BackOfficeExpenses':
      case 'BackOfficeExpenseCategories':
      case 'BackOfficeAddExpense':
      case 'BackOfficeExpenseDetail':
        return 'expenses';
      case 'Reports':
      case 'BackOfficeReports':
      case 'BackOfficeReportPage':
        return 'reports';
      case 'Integrations':
      case 'BackOfficeIntegrations':
      case 'MpesaIntegration':
      case 'MpesaIntegrationConnect':
      case 'MpesaActivationDetails':
      case 'MpesaActivationAuthorise':
        return 'integrations';
      case 'Account':
      case 'Profile':
      case 'BackOfficeProfile':
      case 'BackOfficeCompanyProfile':
      case 'BackOfficeUserProfile':
      case 'BackOfficeProfilePhoto':
      case 'BackOfficeBilling':
      case 'BackOfficeSupport':
        return 'profile';
      default:
        return '';
    }
  }, [activeLeafRoute]);

  const go = useCallback(
    (
      screenName:
        | 'Dashboard'
        | 'People'
        | 'Inventory'
        | 'Orders'
        | 'Sales'
        | 'Suppliers'
        | 'Expenses'
        | 'Reports'
        | 'Integrations'
        | 'Account',
    ) => {
      try {
        navigation.navigate('BackOffice', { screen: screenName });
      } catch {
        navigation.navigate(screenName as any);
      }
    },
    [navigation],
  );

  const handleExitBackOffice = useCallback(() => {
    dispatch(resetBackofficeUnlock());
    const homeReset = CommonActions.reset({
      index: 0,
      routes: [{ name: 'DrawerNavigation', params: { screen: 'Home' } }],
    });
    let nav: any = navigation;
    while (nav) {
      const names: string[] = nav.getState?.()?.routeNames ?? [];
      if (names.includes('DrawerNavigation')) {
        nav.dispatch(homeReset);
        return;
      }
      nav = nav.getParent?.();
    }
    navigation.navigate('DrawerNavigation', { screen: 'Home' });
  }, [dispatch, navigation]);

  return (
    <View style={styles.sidebar}>
      <ScrollView
        style={styles.sidebarNav}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.sidebarNavContent}>
        <SideNavItem
          label="Dashboard"
          icon="appstore"
          iconLibrary="AntDesign"
          isActive={activeNavId === 'dashboard'}
          onPress={() => go('Dashboard')}
          styles={styles}
          theme={theme}
        />
        <SideNavItem
          label="People"
          icon="account-group"
          isActive={activeNavId === 'people'}
          onPress={() => go('People')}
          styles={styles}
          theme={theme}
        />
        <SideNavItem
          label="Inventory"
          icon="inventory"
          iconLibrary="MaterialIcons"
          isActive={activeNavId === 'products'}
          onPress={() => go('Inventory')}
          styles={styles}
          theme={theme}
        />
        <SideNavItem
          label="Orders"
          icon="receipt-sharp"
          iconLibrary="Ionicons"
          isActive={activeNavId === 'orders'}
          onPress={() => go('Orders')}
          styles={styles}
          theme={theme}
        />
        <SideNavItem
          label="Sales"
          icon="chart-areaspline-variant"
          isActive={activeNavId === 'sales'}
          onPress={() => go('Sales')}
          styles={styles}
          theme={theme}
        />
        <SideNavItem
          label="Suppliers"
          icon="truck-delivery"
          isActive={activeNavId === 'suppliers'}
          onPress={() => go('Suppliers')}
          styles={styles}
          theme={theme}
        />
        <SideNavItem
          label="Expenses"
          icon="cash-plus"
          isActive={activeNavId === 'expenses'}
          onPress={() => go('Expenses')}
          styles={styles}
          theme={theme}
        />
        <SideNavItem
          label="Reports"
          icon="file-chart"
          isActive={activeNavId === 'reports'}
          onPress={() => go('Reports')}
          styles={styles}
          theme={theme}
        />
        <SideNavItem
          label="Integrations"
          icon="puzzle"
          isActive={activeNavId === 'integrations'}
          onPress={() => go('Integrations')}
          styles={styles}
          theme={theme}
        />
        <SideNavItem
          label="Account"
          icon="account-circle"
          isActive={activeNavId === 'profile'}
          onPress={() => go('Account')}
          styles={styles}
          theme={theme}
        />
      </ScrollView>

      <View style={styles.sidebarFooter}>
        <Pressable
          onPress={handleExitBackOffice}
          style={({ hovered }: any) => [
            styles.exitButton,
            hovered && styles.exitButtonHovered,
          ]}>
          <Ionicons
            name="exit-outline"
            size={iconSize(18)}
            color={theme.colors.error}
          />
          <Text style={styles.exitButtonText}>Exit Back Office</Text>
        </Pressable>
      </View>
    </View>
  );
}

const createStyles = (theme: any) => {
  const colors = theme.colors;
  const sidebarWidth = s(WEB_NAV_SIDEBAR_RAIL_S);
  return StyleSheet.create({
    sidebar: {
      width: sidebarWidth,
      minWidth: sidebarWidth,
      maxWidth: sidebarWidth,
      flexShrink: 0,
      flexGrow: 0,
      overflow: 'hidden',
      backgroundColor: colors.surface,
      flexDirection: 'column',
    },
    sidebarNav: {
      flex: 1,
      borderRightWidth: borderWidth(1),
      borderRightColor: colors.border,
    },
    sidebarNavContent: {
      paddingTop: v(8),
      paddingBottom: v(8),
      paddingHorizontal: s(3),
      gap: v(2),
    },
    sideNavItem: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: v(4),
      paddingVertical: v(10),
      paddingHorizontal: s(8),
      borderRadius: theme.borderRadius.md,
      minHeight: v(64),
    },
    sideNavItemActive: {
      backgroundColor: withOpacity(colors.primaryDark, 0.12),
    },
    sideNavItemHovered: {
      backgroundColor: colors.surfaceSecondary,
    },
    sideNavIconWrapper: {
      position: 'relative',
      width: s(28),
      height: s(28),
      alignItems: 'center',
      justifyContent: 'center',
    },
    sideNavItemText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(11),
      color: colors.textGray600,
      textAlign: 'center',
      lineHeight: fs(11),
    },
    sideNavItemTextActive: {
      color: colors.primaryDark,
    },
    sideNavItemTextHovered: {
      color: colors.text,
    },
    sidebarFooter: {
      borderTopWidth: borderWidth(1),
      borderTopColor: colors.border,
      borderRightWidth: borderWidth(1),
      borderRightColor: colors.border,
      padding: s(8),
      alignItems: 'center',
    },
    exitButton: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: v(4),
      padding: s(8),
      borderRadius: theme.borderRadius.md,
      width: '100%',
    },
    exitButtonHovered: {
      backgroundColor: withOpacity(colors.error, 0.1),
    },
    exitButtonText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(9),
      color: colors.error,
      textAlign: 'center',
    },
  });
};
